import { HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Spinner, StateView } from '@/components/ui/Feedback';
import { useAuthStore } from '@/store/authStore';
import { useDoctorProfilesStore } from '@/store/doctorProfilesStore';
import { EmailCodeScreen } from './EmailCodeScreen';
import { RegistrationProfilesScreen } from './RegistrationProfilesScreen';
import { RegistrationConsentScreen } from './RegistrationConsentScreen';
import { RegistrationSurveyScreen } from './RegistrationSurveyScreen';

/**
 * Маршрутизатор сценария регистрации.
 *
 * Бэкенд различает всего три шага: подтверждение почты, незавершённая
 * регистрация и завершённая. Внутри незавершённой порядок определяется
 * данными пользователя, а не отдельным шагом от сервера: сначала профиль,
 * затем согласие, затем анкета. Так экран всегда соответствует тому, чего
 * действительно не хватает.
 */
export function RegistrationFlow() {
  const step = useAuthStore((state) => state.step);
  const user = useAuthStore((state) => state.user);
  const activeId = useDoctorProfilesStore((state) => state.activeId);

  if (step === 'email_confirmation' || step === 'email_code') return <EmailCodeScreen />;

  // Промежуточное состояние: сервер переведёт в authenticated ближайшим ответом
  if (step === 'completed' || step === 'complete') return <Waiting />;

  // Прямые шаги от сервера, если он их когда-нибудь вернёт
  if (step === 'consent') return <RegistrationConsentScreen />;
  if (step === 'survey') return <RegistrationSurveyScreen />;

  if (step === 'registration_profile') {
    const profileId = user?.active_doctor_profile_id ?? activeId;
    if (!profileId) return <RegistrationProfilesScreen />;
    if (user?.consent !== true) return <RegistrationConsentScreen />;
    return <RegistrationSurveyScreen />;
  }

  // Шаг неизвестен: сервер его не прислал и определить не удалось. Пустой
  // экран здесь недопустим — из него нет выхода
  return <UnknownStep />;
}

function UnknownStep() {
  const authenticate = useAuthStore((state) => state.authenticate);

  return (
    <div
      className="mx-auto flex w-full max-w-[520px] flex-col justify-center px-5"
      style={{ minHeight: 'var(--bd-viewport-height)' }}
    >
      <StateView
        icon={<HelpCircle className="h-6 w-6" aria-hidden />}
        title="Не удалось продолжить"
        description="Сервер не сообщил, на каком шаге вы остановились. Попробуйте войти заново."
        primary={
          <Button variant="primary" block onClick={() => void authenticate()}>
            Войти заново
          </Button>
        }
      />
    </div>
  );
}

function Waiting() {
  return (
    <div
      className="mx-auto flex w-full max-w-[520px] items-center justify-center"
      style={{ minHeight: 'var(--bd-viewport-height)' }}
    >
      <Spinner />
    </div>
  );
}
